import * as React from "react";
import { useState, useMemo, MouseEvent, ChangeEvent, Dispatch, SetStateAction, useEffect } from "react";
import { DateTime } from "luxon";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import Toolbar from "@mui/material/Toolbar";
import { Button, Link, TextField, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TablePagination from "@mui/material/TablePagination";
import { getComparator, Order, stableSort } from "../../../utils/Table";
import { IToolboxBuild } from "../../../services/registry/ToolboxBuildService";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { MoreVert } from "@mui/icons-material";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";

interface HeadCell {
  id: keyof IToolboxBuild | string;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "ID",
    padding: "none",
  },
  {
    id: "name",
    label: "Name",
  },
  {
    id: "branch",
    label: "Branch",
  },
  {
    id: "version",
    label: "Version",
  },
  {
    id: "arch",
    label: "Architecture",
  },
  {
    id: "creationDate",
    label: "Creation Date",
  },
  {
    id: "commitHash",
    label: "Commit Hash",
  },
];

interface ToolboxBuildsTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: keyof IToolboxBuild | string) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const ToolboxBuildsTableHead = ({ order, orderBy, onRequestSort }: ToolboxBuildsTableHeadProps) => {
  const createSortHandler = (property: keyof IToolboxBuild | string) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell padding="normal" />
      </TableRow>
    </TableHead>
  );
};

interface ToolboxBuildsTableToolbarProps {
  onUploadButtonClick: () => void;
  filterQuery: string;
  setFilterQuery: (value: string) => void;
  onRefreshButtonClick: () => void;
}

const ToolboxBuildsTableToolbar = ({
  onUploadButtonClick,
  filterQuery,
  setFilterQuery,
  onRefreshButtonClick,
}: ToolboxBuildsTableToolbarProps) => {
  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
      <Button variant="outlined" startIcon={<AddIcon />} onClick={onUploadButtonClick} style={{ height: 40 }}>
        Upload
      </Button>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefreshButtonClick} style={{ marginLeft: 8 }}>
        Refresh
      </Button>
      <TextField
        id="outlined-search"
        label="Search toolbox builds"
        type="search"
        size="small"
        onChange={(e) => setFilterQuery(e.target.value)}
        sx={{ width: "360px", marginLeft: 1 }}
        value={filterQuery}
      />
    </Toolbar>
  );
};

export interface ToolboxBuildsTableProps {
  rows: IToolboxBuild[];
  selectedItem: IToolboxBuild | undefined;
  setSelectedItem: Dispatch<SetStateAction<IToolboxBuild | undefined>>;
  onUploadButtonClick: () => void;
  onEditButtonClick: () => void;
  onRemoveButtonClick: () => void;
  initialFilterQuery: string | undefined;
  onRefreshButtonClick: () => void;
}

export const ToolboxBuildsTable = ({
  rows,
  selectedItem,
  setSelectedItem,
  onUploadButtonClick,
  onEditButtonClick,
  onRemoveButtonClick,
  initialFilterQuery,
  onRefreshButtonClick,
}: ToolboxBuildsTableProps) => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof IToolboxBuild | string>("creationDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterQuery, setFilterQuery] = useState<string>("");
  const [menuAnchor, setMenuAnchor] = useState<Element | undefined>(undefined);

  useEffect(() => {
    if (initialFilterQuery) {
      setFilterQuery(initialFilterQuery);
    }
  }, [initialFilterQuery]);

  const requestSortHandler = (event: MouseEvent<unknown>, property: keyof IToolboxBuild | string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const pageChangeHandler = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rowsPerPageChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const menuOpenHandler = (event: MouseEvent<HTMLElement>, item: IToolboxBuild) => {
    setMenuAnchor(event.target as Element);
    setSelectedItem(item);
  };

  const menuCloseHandler = () => {
    setMenuAnchor(undefined);
  };

  const editMenuClickHandler = () => {
    onEditButtonClick();
    menuCloseHandler();
  };

  const removeMenuClickHandler = () => {
    onRemoveButtonClick();
    menuCloseHandler();
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const filteredRows = useMemo(() => {
    const query = filterQuery.toLowerCase();
    return rows.filter((row) => {
      return (
        row.id.includes(query) ||
        row.name?.toLowerCase().includes(query) ||
        row.version?.toLowerCase().includes(query) ||
        row.branch?.toLowerCase().includes(query) ||
        row.arch.toLowerCase().includes(query) ||
        (row.commitHash && row.commitHash.includes(query))
      );
    });
  }, [rows, filterQuery]);

  const visibleRows = useMemo(() => {
    return stableSort(filteredRows, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  const isSelected = (row: IToolboxBuild, selectedItem: IToolboxBuild | undefined) =>
    selectedItem !== undefined && row.id === selectedItem.id;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <ToolboxBuildsTableToolbar
          onUploadButtonClick={onUploadButtonClick}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          onRefreshButtonClick={onRefreshButtonClick}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} size="small">
            <ToolboxBuildsTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={requestSortHandler}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `toolbox-builds-table-checkbox-${index}`;
                const creationDate = DateTime.fromSeconds(row.creationDate).toLocaleString(
                  DateTime.DATETIME_SHORT_WITH_SECONDS,
                );
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    onClick={() => setSelectedItem(row)}
                    tabIndex={-1}
                    key={row.id}
                    selected={isSelected(row, selectedItem)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="normal" />
                    <TableCell component="th" id={labelId} scope="row" padding="none" style={{ fontFamily: "monospace" }}>
                      {row.id}
                    </TableCell>
                    <TableCell align="left">{row.name}</TableCell>
                    <TableCell align="left">{row.branch}</TableCell>
                    <TableCell align="left">{row.version}</TableCell>
                    <TableCell align="left">{row.arch}</TableCell>
                    <TableCell align="left">{creationDate}</TableCell>
                    <TableCell align="left" style={{ fontFamily: "monospace" }}>
                      {row.commitHash && (
                        <Link
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              `https://github.com/FortisGames/proj-townsfolk-game-platform/commit/${row.commitHash}`,
                            );
                          }}
                        >
                          {row.commitHash}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          menuOpenHandler(e, row);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={pageChangeHandler}
          onRowsPerPageChange={rowsPerPageChangeHandler}
        />
        <Menu anchorEl={menuAnchor} keepMounted open={menuAnchor !== undefined} onClose={() => menuCloseHandler()}>
          <MenuItem onClick={() => editMenuClickHandler()}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Edit</Typography>
          </MenuItem>
          <MenuItem onClick={() => removeMenuClickHandler()}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Remove</Typography>
          </MenuItem>
        </Menu>
      </Paper>
    </Box>
  );
};
